import { useQuery, useMutation } from '@apollo/client';
import { CREATE_AI_FEATURE } from '@nile/banyan/src/Graphql/Mutations/ai';
import { GET_AI_CONFIG } from '@nile/banyan/src/Graphql/Query/ai';
import AIPromptEditor from '@nile/banyan/src/Pages/Protected/Index/AIPromptEditor';
import {
  AccessControl,
  EmojiRender,
  EventEmitter,
  getTranslatedText,
  useActivityTracker,
  useIsAuthorisedHook,
  useUserMSPCheckHook,
  withErrorBoundary,
  withPlanFeatureGating,
  AI_AGENTS_CONSTANT_FEATURE_ID_MAP
} from '@nile/components';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { AccountClient } from '@nile/graphql/src';
import { SVGIcon } from '@superops/super-icons';
import {
  ActionList,
  Button,
  Popover,
  TextStyle
} from '@superops/ui-design-system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tooltip } from 'react-tippy';

const AI = {
  COMPOSE: 'global.ai.ticket.sideConversation.compose',
  SUMMARIZATION: 'global.ai.ticket.summarization'
};

const AI_TYPE = {
  REPHRASE: 'REPHRASE'
};

const ToolTipWrapper = (props) => {
  const { title, children, showTooltip } = props;

  if (showTooltip) {
    return (
      <Tooltip
        title={title}
        inertia
        trigger="mouseenter"
        animation="shift"
        animateFill={false}
        className="tooltip"
        position="bottom"
      >
        {children}
      </Tooltip>
    );
  } else {
    return <>{children}</>;
  }
};

const PromptList = (props) => {
  const { prompt } = props;

  return (
    <div className="flex g5">
      <div className="flex">
        <EmojiRender emoji={prompt?.displayIcon} />
      </div>

      <div>
        <TextStyle>{prompt?.name}</TextStyle>
      </div>
    </div>
  );
};

const AIComponent = (props) => {
  const {
    data,
    onSummaryTypeChange,
    onCopy,
    hideCopyPaste,
    type,
    readOnly,
    hideButtonText,
    prompts,
    editorRef,
    buttonText,
    sendFeedback,
    featureName,
    uiType,
    option,
    workItem,
    summaryType,
    summaryCase,
    variables,
    onOptionClick,
    successText,
    headingText,
    headingIcon,
    copyButtonText = null,
    copySuccessText = null,
    loadingText = null,
    mixpanelEventOnClick = null,
    mixpanelEventOnCopy = null,
    buttonVariant,
    buttonSize,
    iconOnly = false
  } = props;

  const intl = useIntl();
  const aiFeatureHook = AIFeaturesHook();
  const flags = useFlags();

  const [rephrasePopoverActive, setRephrasePopoverActive] = useState(false);
  const [promptEditorActive, setPromptEditorActive] = useState(false);
  const [createPromptLoading, setCreatePromptLoading] = useState(false);

  const [selectedText, setSelectedText] = useState(null);
  const [localPrompts, setLocalPrompts] = useState([]);

  const [createAIFeatureMutation] = useMutation(CREATE_AI_FEATURE, {
    client: AccountClient
  });

  const promptBuilderData = aiFeatureHook?.getAIFeatureData('PROMPT_BUILDER');
  const totalPrompts = promptBuilderData?.subFeatures || [];
  const isPromptLimitExceeded = totalPrompts.length >= 5;

  // Sync local prompts with props
  useEffect(() => {
    if (prompts) {
      setLocalPrompts(prompts);
    }
  }, [prompts]);

  const updateSelectedText = (updated) => {
    if (selectedText !== updated) {
      setSelectedText(updated);
    }
  };

  const getSelectedText = () => {
    if (editorRef.current?.FroalaEditorRef) {
      if (editorRef.current?.FroalaEditorRef?.current?.selection) {
        return editorRef.current?.FroalaEditorRef?.current?.selection.text();
      }
      return null;
    } else {
      const range = editorRef.current?.getEditorSelection();
      if (range) {
        const editor = editorRef.current?.getEditor();
        const unprivilegedEditor = editorRef.current?.makeUnprivilegedEditor(
          editor
        );
        // You may now use the unprivilegedEditor proxy methods
        return unprivilegedEditor?.getText(range.index, range.length);
      }
      return null;
    }
  };

  const toggleRephrasePopover = () => {
    const updatedState = !rephrasePopoverActive;
    setRephrasePopoverActive(updatedState);
  };

  const getSelectedTextOnHover = () => {
    const selected = getSelectedText();
    if (selected) {
      updateSelectedText(selected);
    } else {
      setSelectedText(null);
    }
  };

  const [isAISummaryOpen, setIsAISummaryOpen] = useState(false);

  const isInSideConversation =
    data?.variables?.input?.summaryCase === 'SIDE_CONVERSATION' ||
    variables?.input?.summaryCase === 'SIDE_CONVERSATION';

  const isEnabled = aiFeatureHook?.getIsAIFeatureEnabled(featureName) || false;

  // data
  // {
  //   variables: variables sent for API Input,
  //   Api: GraphQl API,
  //   client: GraphQlClient,
  //   summaryType: initialSummaryType,
  //   accessorKey: If we pass custom API we need to pass accessorKey
  // }

  //  onSummaryTypeChange: a function that should return input variables with updated summary type

  // OnCopy: A function that gets content from AI summary as params

  const isMSP = useUserMSPCheckHook();

  const openAISummaryContainer = () => {
    if (mixpanelEventOnClick) {
      useActivityTracker(mixpanelEventOnClick);
    } else {
      useActivityTracker({
        event: `Clicked AI summarization from ${summaryCase}`
      });
    }

    setIsAISummaryOpen(true);
    // PASS NECESSARY DATA HERE TO BE AVAILABLE FOR SUMMARY COMPONENT
    EventEmitter.dispatch('openAISummary', {
      data,
      onCopy,
      hideCopyPaste,
      workItem,
      summaryCase,
      summaryType,
      variables,
      successText: successText || null,
      headingText,
      headingIcon,
      copyButtonText,
      copySuccessText,
      loadingText,
      type
    });
  };

  const openAIRephrase = () => {
    setIsAISummaryOpen(true);
    if (mixpanelEventOnClick) {
      useActivityTracker(mixpanelEventOnClick);
    }

    EventEmitter.dispatch('openAISummary', {
      data,
      hideCopyPaste,
      // api,
      type: AI_TYPE.REPHRASE,
      successText: successText || null,
      headingText,
      headingIcon,
      copyButtonText,
      copySuccessText,
      loadingText,
      mixpanelEventOnCopy: mixpanelEventOnCopy || null,
      beta: true,
      hideFeedback: true
    });
  };

  const rephraseContent = (prompt) => {
    toggleRephrasePopover();
    const variables = {
      featureId: prompt.featureId,
      text: selectedText
    };

    const updatedData = {
      ...data,
      variables: variables
    };

    if (selectedText) {
      if (mixpanelEventOnClick) {
        useActivityTracker(mixpanelEventOnClick);
      }

      EventEmitter.dispatch('openAISummary', {
        data: updatedData,
        type: AI_TYPE.REPHRASE,
        prompts: localPrompts,
        currentPrompt: prompt,
        onCopy,
        selectedText: selectedText,
        sendFeedback: sendFeedback,
        hideCustomFeedback: true,
        headingText,
        headingIcon,
        copyButtonText,
        copySuccessText,
        loadingText,
        successText: successText || null,
        mixpanelEventOnCopy: mixpanelEventOnCopy || null,
        beta: true
      });
    }
  };

  const closeAISummary = () => {
    setIsAISummaryOpen(false);
    setSelectedText(null);
  };

  const openPromptEditor = () => {
    if (isPromptLimitExceeded) {
      return;
    }
    toggleRephrasePopover();
    setPromptEditorActive(true);
  };

  useEffect(() => {
    if (promptEditorActive) {
      // Trigger the AIPromptEditor button click after it renders
      const timer = setTimeout(() => {
        const container = document.getElementById('ai-prompt-editor-trigger');
        if (container) {
          const button = container.querySelector('button');
          if (button) {
            button.click();
          }
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [promptEditorActive]);

  const closePromptEditor = () => {
    setPromptEditorActive(false);
  };

  const createPrompt = async (params) => {
    const promptData = params?.data;

    if (!promptData) {
      return;
    }

    const variables = {
      input: {
        parentFeature: {
          featureId: AI_AGENTS_CONSTANT_FEATURE_ID_MAP.PROMPT_BUILDER
        },
        state: 'ACTIVE',
        name: promptData?.name,
        description: promptData?.description,
        prompt: promptData?.prompt,
        displayIcon: promptData?.displayIcon
      }
    };

    try {
      setCreatePromptLoading(true);
      const res = await createAIFeatureMutation({
        variables: variables
      });
      if (res) {
        setCreatePromptLoading(false);
        setPromptEditorActive(false);

        // Add the newly created prompt to local prompts list
        const newPrompt = {
          featureId: res?.data?.createAIFeature?.featureId,
          name: promptData?.name,
          description: promptData?.description,
          prompt: promptData?.prompt,
          displayIcon: promptData?.displayIcon,
          state: 'ACTIVE'
        };

        setLocalPrompts((prevPrompts) => [...prevPrompts, newPrompt]);

        // Refresh AI features data
        aiFeatureHook?.getAIFeaturesData();
      }
    } catch (error) {
      console.error('Error creating prompt:', error);
      setCreatePromptLoading(false);
    }
  };

  useEffect(() => {
    EventEmitter.subscribe('AISummaryClosed', closeAISummary);
    EventEmitter.subscribe('globalAIBackClicked', closeAISummary);
    return function cleanup() {
      EventEmitter.unsubscribe('AISummaryClosed');
      EventEmitter.unsubscribe('globalAIBackClicked');
    };
  }, []);

  if (!isMSP) return null;

  if (uiType === 'Option') {
    const disabled = !isEnabled;
    const disabledTitle =
      option?.disabledTitle ||
      getTranslatedText(intl, 'app.ai.features.enable.tooltip');
    const enabledTitle =
      option?.enabledTitle ||
      getTranslatedText(
        intl,
        'ticketDetails.aiSummarization.tooltip.getAiSummary'
      );

    const onClick = () => {
      if (!isAISummaryOpen && !disabled) {
        if (onOptionClick) {
          onOptionClick();
          openAISummaryContainer();
        }
      }
    };
    return (
      <ToolTipWrapper
        showTooltip={disabled}
        title={disabled ? disabledTitle : enabledTitle}
      >
        <Button
          icon={
            option?.icon && (
              <div className="ignoreOverride">
                <SVGIcon name={option?.icon} />{' '}
              </div>
            )
          }
          aiPrimary
          size="slim"
          disabled={disabled}
          onClick={onClick}
        >
          {!iconOnly && (
            <div className="fsize12">
              <FormattedMessage id={option?.label} />
            </div>
          )}
        </Button>
      </ToolTipWrapper>
    );
  }

  if (type === AI_TYPE.REPHRASE && isMSP) {
    const isRephraseDisabled = !isEnabled || isEmpty(localPrompts);

    const AIClassNames = [
      'flex',
      isRephraseDisabled && 'opacity5',
      !selectedText && 'cursor-not-allowed',
      isEmpty(localPrompts) && 'cursor-not-allowed'
    ]
      .filter(Boolean)
      .join(' ');

    const getTooltipMessage = () => {
      if (!isEnabled) {
        return getTranslatedText(intl, 'ai.rephrase.disabled.tooltip');
      } else if (isEmpty(localPrompts)) {
        return getTranslatedText(intl, 'ai.rephrase.prompts.empty.tooltip');
      } else if (!selectedText) {
        return getTranslatedText(
          intl,
          'ai.rephrase.selectedText.empty.message'
        );
      } else {
        return getTranslatedText(intl, 'common.rephrase');
      }
    };

    const tooltipMessage = getTooltipMessage();

    const togglePopover = () => {
      if (isEnabled && !isEmpty(localPrompts) && selectedText) {
        toggleRephrasePopover();
      }
    };

    const getButtonVariant = () => {
      switch (buttonVariant) {
        case 'primary':
          return {
            primary: true
          };
        case 'secondary':
          return {
            secondary: true
          };
        case 'plain':
          return {
            plain: true
          };

        case 'default':
          return {};
        default:
          return {
            plain: true
          };
      }
    };

    const buttonVariantData = getButtonVariant();

    return (
      <>
        {readOnly ? (
          <div className="flex g10">
            <div className="flex ignoreOverride">
              <SVGIcon name="AIWand" />
            </div>
            {!hideButtonText && (
              <div>
                <Button plain onClick={openAIRephrase}>
                  {buttonText || <FormattedMessage id="common.rephrase" />}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Tooltip
            title={tooltipMessage}
            inertia
            trigger="mouseenter"
            animation="shift"
            animateFill={false}
            className="tooltip"
            position="bottom"
          >
            <div
              className={AIClassNames}
              id="ai-rephrase"
              // The getSelectedText is not working in ActionContainer due to some reasons
              // The selection is cleared in editor need to fix it.
              onMouseOver={getSelectedTextOnHover}
            >
              <Popover
                active={rephrasePopoverActive}
                customClassName="flex"
                activator={
                  <Button
                    onClick={togglePopover}
                    icon={
                      <div
                        className={`ignoreOverride ${
                          !selectedText ? 'cursor-not-allowed' : ''
                        }`}
                      >
                        <SVGIcon name={props?.icon || 'AIWand'} />
                      </div>
                    }
                    disabled={isRephraseDisabled}
                    {...buttonVariantData}
                    size={buttonSize}
                  >
                    {!iconOnly && !hideButtonText && (
                      <div className={!selectedText && 'cursor-not-allowed'}>
                        {buttonText || (
                          <FormattedMessage id="common.rephrase" />
                        )}
                      </div>
                    )}
                  </Button>
                }
                onClose={toggleRephrasePopover}
                zIndexOverride={1000}
              >
                {!isEmpty(localPrompts) && (
                  <>
                    <TextStyle variation="subdued" uppercase>
                      <div className="pl10 pt8 mbm-5">
                        <FormattedMessage id="common.rephrase.text" />
                      </div>
                    </TextStyle>
                    <ActionList
                      actionRole="menuitem"
                      sections={[
                        {
                          items: localPrompts?.map((prompt) => {
                            return {
                              content: <PromptList prompt={prompt} />,
                              onAction: () => {
                                rephraseContent(prompt);
                              }
                            };
                          })
                        },
                        {
                          items: [
                            flags?.marketplace && {
                              content: (
                                <Tooltip
                                  title={getTranslatedText(
                                    intl,
                                    isPromptLimitExceeded
                                      ? 'settings.ai.prompt.add.disabled.tooltip'
                                      : 'settings.ai.prompt.add.tooltip'
                                  )}
                                >
                                  <div
                                    className={`flex g5 linkText ${
                                      isPromptLimitExceeded ? 'disabled' : ''
                                    }`}
                                  >
                                    <div className="flex">
                                      <SVGIcon
                                        color="#091EF5"
                                        name="CirclePlus"
                                      />
                                    </div>
                                    <div>
                                      <TextStyle>
                                        <FormattedMessage id="settings.ai.page.prompt.create.title" />
                                      </TextStyle>
                                    </div>
                                  </div>
                                </Tooltip>
                              ),
                              onAction: openPromptEditor
                            }
                          ]
                        }
                      ]}
                    />
                  </>
                )}
              </Popover>
            </div>
          </Tooltip>
        )}

        {promptEditorActive && (
          <div id="ai-prompt-editor-trigger" style={{ display: 'none' }}>
            <AIPromptEditor
              saveLoading={createPromptLoading}
              onSave={createPrompt}
              onClose={closePromptEditor}
              prompt={null}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <Tooltip
      title={getTranslatedText(
        intl,
        !isEnabled
          ? 'app.ai.features.enable.tooltip'
          : isInSideConversation
          ? 'ticketDetails.aiSummarization.tooltip.composeWithAi'
          : 'ticketDetails.aiSummarization.tooltip.getAiSummary'
      )}
      inertia
      trigger="mouseenter"
      animation="shift"
      animateFill={false}
      className="tooltip"
      position="bottom"
    >
      <div
        className={`${isEnabled ? 'pointer' : 'disabled opacity5'} flex`}
        onClick={!isAISummaryOpen && isEnabled && openAISummaryContainer}
      >
        <SVGIcon name="AskAI" />
      </div>
    </Tooltip>
  );
};

export default withPlanFeatureGating(withErrorBoundary(AIComponent), {
  feature: 'AI_FEATURES',
  mode: 'hide',
  id: 'AIComponent'
});
